import { useEffect, useRef, useState } from "react";
import { ReactComponent as CloseSvg } from "../../assets/icons/close.svg";
import { ReactComponent as CreditIcon } from "../../assets/icons/credit.svg";
import { ReactComponent as InfoIcon } from "../../assets/icons/info-circle.svg";
import { ReactComponent as CollectionDetailsIcon } from "../../assets/icons/collectionDetails.svg";

import {
  ModalContainer,
  ModalContent,
  ModalFooter,
  TitleContainer,
  ModalTitle,
  TitleRow,
  IconButton,
  Column,
  LabelRow,
  LabelText,
  Textarea,
  AspectRatioContainer,
  AspectRatioOption,
  AspectRatioPreviewWrapper,
  AspectRatioPreview,
  AspectRatioLabel,
  ButtonGroup,
  InfoIconWrapper,
  TooltipContainer,
  TooltipText,
  CheckboxContainerColumn,
  CheckboxContainer,
  CheckboxText,
  SmallRow,
} from "./CreateImageModal.styles";
import Modal from "../utils/Modal/Modal";
import { useLockBodyScroll } from "../../hooks/useLockBodyScroll";
import { useDispatch, useSelector } from "react-redux";
import {
  selectGenerateImageCollectionPending,
  selectToggleToFavoriteLoading,
} from "../../store/slices/model/slice";
import {
  generateImageCollection,
  toggleToFavorite,
} from "../../store/slices/model/asyncThunks";
import { selectUser } from "../../store/slices/user/slice";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import LottieIcon from "../LottieIcon/LottieIcon";
import likeAnimation from "../../assets/animations/like.json";
import settingsAnimation from "../../assets/animations/settings.json";
import checkboxAnimation from "../../assets/animations/radioButton.json";
import ExplosionButton from "../ExplosionButton/ExplosionButton";
import CreateImageSettingsModal from "./CreateImageSettingsModal";
import GenerationToast from "../utils/GenerationToast";

const ASPECT_RATIOS = [
  { id: "1:1", label: "1:1", width: 22, height: 22 },
  { id: "2:3", label: "2:3", width: 14, height: 22 },
  { id: "3:2", label: "3:2", width: 22, height: 14 },
  { id: "9:16", label: "9:16", width: 12, height: 22 },
  { id: "16:9", label: "16:9", width: 22, height: 12 },
];

const CreateImageModal = ({
  isOpen,
  onClose,
  collection,
  playlistId,
  overlayZIndex = 3100,
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const tooltipRef = useRef(null);

  const [prompt, setPrompt] = useState("");
  const [aspectRatio, setAspectRatio] = useState(
    () => localStorage.getItem("createImageAspectRatio") || "1:1",
  );
  const [negativePrompt, setNegativePrompt] = useState("");
  const [quality, setQuality] = useState(
    () => localStorage.getItem("createImageQuality") || "LOW",
  );
  const [isExploding, setIsExploding] = useState(false);
  const [isCustomizeOpen, setIsCustomizeOpen] = useState(false);
  const [shouldEnhancePrompt, setShouldEnhancePrompt] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);

  const [seedNumber, setSeedNumber] = useState(
    Math.floor(Math.random() * 10000000) + 1,
  );

  const user = useSelector(selectUser);
  const loading = useSelector(selectToggleToFavoriteLoading);
  const generateLoading = useSelector(selectGenerateImageCollectionPending);

  const isLoading = loading || generateLoading;

  useLockBodyScroll(isOpen || isCustomizeOpen);

  useEffect(() => {
    if (!showTooltip) return;

    const handleClickOutside = (event) => {
      if (tooltipRef.current && !tooltipRef.current.contains(event.target)) {
        setShowTooltip(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("touchstart", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
    };
  }, [showTooltip]);

  const generateRandomSeed = () => {
    return Math.floor(Math.random() * 10000000) + 1;
  };

  const handleFavoriteToggle = () => {
    if (!loading && collection?._id) {
      dispatch(toggleToFavorite({ collectionId: collection._id }));
    }
  };

  const basePrice = quality === "HIGH" ? 15 : quality === "ULTRA" ? 20 : 10;
  const totalPrice = basePrice + (shouldEnhancePrompt ? 5 : 0);

  const resetStates = () => {
    setPrompt("");
    setNegativePrompt("");
    setShowTooltip(false);
  };

  const handleCancel = () => {
    if (!isLoading) {
      resetStates();
      onClose();
    }
  };

  const handleConfirm = async () => {
    if (!prompt || !prompt.trim()) {
      toast.error("Please enter a prompt");
      return;
    }

    const formData = new FormData();

    formData.append("customPrompt", prompt.trim());
    formData.append("aspectRatio", aspectRatio);
    formData.append("modelId", "69e0d4b7e22ca810bca2172b");
    formData.append("quality", quality);
    formData.append("shouldEnhancePrompt", shouldEnhancePrompt);

    if (negativePrompt && negativePrompt.trim()) {
      formData.append("negativePrompt", negativePrompt.trim());
    }

    if (seedNumber) {
      formData.append("seed", seedNumber);
    } else {
      formData.append("seed", generateRandomSeed());
    }
    if (playlistId) {
      formData.append("playlistId", playlistId);
    }

    dispatch(generateImageCollection(formData))
      .unwrap()
      .then(() => {
        toast.custom(() => (
          <GenerationToast
            successMessage="Generation started..."
            label="View"
            onClick={() => {
              navigate("/collection");
              window.scrollTo({ top: 0, left: 0, behavior: "instant" });
            }}
          />
        ));

        setIsExploding(true);
        setTimeout(() => {
          setIsExploding(false);
          handleCancel();
        }, 500);
      })
      .catch((err) => {
        toast.error(
          err ||
            "An error occurred while generating images. Please try again later.",
        );
      });
  };

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={() => {
          if (!isLoading) {
            onClose();
            resetStates();
          }
        }}
        customOverlayStyles={{ zIndex: overlayZIndex }}
      >
        <ModalContainer>
          <ModalContent>
            <TitleContainer>
              <TitleRow>
                <ModalTitle>{collection?.name || "Text to Image"}</ModalTitle>
                <SmallRow>
                  {collection?._id && (
                    <IconButton
                      onClick={() => {
                        if (isLoading) return;
                        onClose();
                        resetStates();
                        navigate("/create-image", {
                          state: { collection, playlistId },
                        });
                        window.scrollTo({
                          top: 0,
                          left: 0,
                          behavior: "instant",
                        });
                      }}
                    >
                      <CollectionDetailsIcon />
                    </IconButton>
                  )}
                  <IconButton style={{ zIndex: 1 }}>
                    <LottieIcon
                      animationData={likeAnimation}
                      trigger="toggle"
                      isToggled={user?.favoriteCollections?.includes(
                        collection?._id,
                      )}
                      onToggle={handleFavoriteToggle}
                      likeSegment={[1, 90]}
                      unlikeSegment={[90, 180]}
                      size={50}
                      absolute
                    />
                  </IconButton>
                </SmallRow>
              </TitleRow>

              <IconButton
                onClick={() => {
                  if (!isLoading) {
                    onClose();
                    resetStates();
                  }
                }}
              >
                <CloseSvg />
              </IconButton>
            </TitleContainer>

            <Column>
              <LabelRow>
                <LabelText>Prompt</LabelText>
                <InfoIconWrapper
                  ref={tooltipRef}
                  $active={!!collection?.customPrompt}
                  onClick={() => {
                    if (collection?.customPrompt) {
                      setShowTooltip(!showTooltip);
                    }
                  }}
                >
                  <InfoIcon />
                  {showTooltip && collection?.customPrompt && (
                    <TooltipContainer>
                      <TooltipText>{collection.customPrompt}</TooltipText>
                    </TooltipContainer>
                  )}
                </InfoIconWrapper>
              </LabelRow>
              <Textarea
                placeholder={"Describe the image you want to create"}
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                disabled={isLoading}
              />
            </Column>

            <CheckboxContainerColumn>
              <CheckboxContainer
                onClick={() => {
                  setShouldEnhancePrompt(!shouldEnhancePrompt);
                }}
              >
                <LottieIcon
                  animationData={checkboxAnimation}
                  trigger="toggle-reverse"
                  isActive={shouldEnhancePrompt}
                  speed={2}
                  onActiveChange={() => {
                    setShouldEnhancePrompt(!shouldEnhancePrompt);
                  }}
                  onClick={() => {
                    setShouldEnhancePrompt(!shouldEnhancePrompt);
                  }}
                  size={24}
                  strokeColor={"#2877d9"}
                />
                <CheckboxText>Enhance prompt before generation</CheckboxText>
              </CheckboxContainer>
            </CheckboxContainerColumn>

            <Column>
              <LabelText>Aspect Ratio</LabelText>
              <AspectRatioContainer>
                {ASPECT_RATIOS.map((ratio) => {
                  const selected = aspectRatio === ratio.id;
                  return (
                    <AspectRatioOption
                      key={ratio.id}
                      $selected={selected}
                      onClick={() => {
                        if (!isLoading) {
                          setAspectRatio(ratio.id);
                          localStorage.setItem(
                            "createImageAspectRatio",
                            ratio.id,
                          );
                        }
                      }}
                    >
                      <AspectRatioPreviewWrapper>
                        <AspectRatioPreview
                          $selected={selected}
                          $width={ratio.width}
                          $height={ratio.height}
                        />
                      </AspectRatioPreviewWrapper>
                      <AspectRatioLabel>{ratio.label}</AspectRatioLabel>
                    </AspectRatioOption>
                  );
                })}
              </AspectRatioContainer>
            </Column>
          </ModalContent>

          <ModalFooter>
            <ButtonGroup>
              <LottieIcon
                animationData={settingsAnimation}
                trigger="hover"
                strokeColor={"#fff"}
                size={28}
                onClick={() => !isLoading && setIsCustomizeOpen(true)}
              />
              <ExplosionButton
                style={{ margin: "0px 6px", flex: 1 }}
                disabled={generateLoading}
                onClick={handleConfirm}
                isLoading={generateLoading}
                isExploding={isExploding}
                small
              >
                {generateLoading ? (
                  <l-line-spinner
                    size="16"
                    stroke="1.25"
                    speed="1.5"
                    color="black"
                  />
                ) : (
                  <>
                    Generate
                    <CreditIcon />
                    {totalPrice}
                  </>
                )}
              </ExplosionButton>
            </ButtonGroup>
          </ModalFooter>
        </ModalContainer>
      </Modal>

      <CreateImageSettingsModal
        isOpen={isCustomizeOpen}
        onClose={() => setIsCustomizeOpen(false)}
        seedNumber={seedNumber}
        setSeedNumber={setSeedNumber}
        negativePrompt={negativePrompt}
        setNegativePrompt={setNegativePrompt}
        quality={quality}
        setQuality={setQuality}
        totalPrice={totalPrice}
        collectionSeed={collection?.seed}
        overlayZIndex={overlayZIndex + 200}
        resetSeed={isOpen}
      />
    </>
  );
};

export default CreateImageModal;
